<template>
  <div ref="chartContainer" class="bg-white border border-gray-200 rounded-lg flex flex-col">
    <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 rounded-t-lg">
      <h3 class="font-medium text-blue-900 text-sm">Performance Trend</h3>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-[400px]">
      <div class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
    </div>
    <div v-else-if="chartData.series.length > 0" class="h-[400px]">
      <Chart_V5
        :key="chartKey"
        :chart_data="chartData"
        :chart_config="chartConfig"
        override-height="h-[390px]"
      />
    </div>
    <div v-else class="flex items-center justify-center h-[200px] text-gray-400 text-sm">
      No time series data available
    </div>

    <!-- Controls -->
    <div v-if="chartData.series.length > 0" class="border-t border-gray-200">
      <div class="px-3 py-1.5 flex items-center justify-center gap-3 text-xs text-gray-600">
        <label v-if="isRate" class="flex items-center gap-1">
          <span class="font-medium">View</span>
          <select v-model="viewMode" @change="chartKey++" class="border border-gray-200 rounded px-1.5 py-0.5 text-xs">
            <option value="rate">Rate</option>
            <option value="numerator">Numerator</option>
          </select>
        </label>
        <label v-if="!showAllObservations" class="flex items-center gap-1">
          <span class="font-medium">Period</span>
          <select v-model="performancePeriod" @change="chartKey++" class="border border-gray-200 rounded px-1.5 py-0.5 text-xs">
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import { groupBy } from 'lodash';
import { format, subYears } from 'date-fns';

export default {
  name: 'PerformanceTrend',
  components: { Chart_V5 },
  props: {
    metricIds: { type: Array, default: () => [] },
    year: { type: Number, required: true },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      timeSeriesData: null,
      metricMeta: null,
      chartKey: 0,
      viewMode: 'rate',
      performancePeriod: 'week',
      resizeObserver: null,
      resizeDebounceTimer: null,
    };
  },
  computed: {
    metricId() {
      return this.metricIds.length ? this.metricIds[0] : null;
    },
    metricName() {
      return this.metricMeta?.name || 'Metric';
    },
    displayConfig() {
      return this.metricMeta?.display_config || {};
    },
    isPercentage() {
      return this.displayConfig.format === 'percentage' ||
        this.metricMeta?.composable_config?.aggregation_type === 'percentage';
    },
    isCurrency() {
      return this.displayConfig.format === 'currency';
    },
    showAllObservations() {
      return this.displayConfig.show_all_observations === true;
    },
    isDecimal() {
      return this.displayConfig.format === 'decimal' || this.displayConfig.format === 'number';
    },
    isRate() {
      return this.isPercentage || this.isDecimal || this.isCurrency
        || this.displayConfig.format === 'per_1k';
    },
    isLowerBetter() {
      return this.displayConfig.higher_is_better === false;
    },
    metadataSubtitle() {
      const results = this.timeSeriesData?.results;
      if (!results?.length) return null;
      const relevant = this.showAllObservations
        ? results
        : results.filter(r => parseInt(r.observation_date.substring(0, 4), 10) === this.year);
      if (!relevant.length) return null;
      const latest = relevant.reduce((a, b) => (a.observation_date > b.observation_date ? a : b));
      const md = latest.metadata;
      if (!md?.denom_start_date) return null;
      const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${fmt(md.denom_start_date)} – ${fmt(md.denom_end_date)}`;
    },
    decimalPlaces() {
      return this.displayConfig.decimal_places ?? 1;
    },
    aggregatedData() {
      if (!this.timeSeriesData?.results?.length) return [];

      // Aggregate across entities per observation_date (handles entity drill-in
      // where backend returns per-child rows)
      const byDate = {};
      for (const r of this.timeSeriesData.results) {
        const key = r.observation_date;
        if (!byDate[key]) {
          byDate[key] = {
            observation_date: key,
            numerator: 0,
            denominator: 0,
            numerator_efctv: null,
            denominator_efctv: null,
          };
        }
        byDate[key].numerator += r.numerator || 0;
        byDate[key].denominator += r.denominator || 0;
        if (r.numerator_efctv != null) {
          byDate[key].numerator_efctv = (byDate[key].numerator_efctv || 0) + r.numerator_efctv;
          byDate[key].denominator_efctv = (byDate[key].denominator_efctv || 0) + (r.denominator_efctv || 0);
        }
      }

      let data = Object.values(byDate).sort((a, b) =>
        a.observation_date.localeCompare(b.observation_date)
      );

      if (this.performancePeriod === 'month') {
        data = this.aggregateToMonthly(data);
      }

      return data;
    },
    chartData() {
      const data = this.aggregatedData;
      if (!data.length) return { series: [], labels: [] };

      const isNumeratorMode = this.isRate && this.viewMode === 'numerator';
      const isCount = !this.isRate || isNumeratorMode;
      const rate = isCount
        ? r => r.numerator
        : r => (r.denominator > 0 ? r.numerator / r.denominator : 0);

      // All-observations mode: display everything the backend returned, no client-side year filtering
      if (this.showAllObservations) {
        if (!data.length) return { series: [], labels: [] };
        const labels = data.map(r => r.observation_date.slice(0, 10));
        const labelToRow = {};
        data.forEach((r, i) => { labelToRow[labels[i]] = r; });
        const series = [{
          values: data.map(rate),
          config: {
            label: this.metricName,
            backgroundColor: 'rgba(75, 192, 192, 0.4)',
            type: 'bar',
            yAxis: 'left',
            offset: 0,
            tooltip: {
              formatter: (d) => this.currentYearTooltip(d, labelToRow[d.label], isCount),
            },
          },
        }];
        return { series, labels };
      }

      const currentYear = data.filter(r =>
        parseInt(r.observation_date.substring(0, 4), 10) === this.year
      );
      const priorYear = data.filter(r =>
        parseInt(r.observation_date.substring(0, 4), 10) === this.year - 1
      );

      if (!currentYear.length) return { series: [], labels: [] };

      const labels = currentYear.map(r => r.observation_date.slice(0, 10));
      const labelToCurrentRow = {};
      currentYear.forEach((r, i) => { labelToCurrentRow[labels[i]] = r; });
      const series = [];

      // Current year bars
      series.push({
        values: currentYear.map(rate),
        config: {
          label: isCount ? 'Count' : `${this.year} Performance`,
          backgroundColor: 'rgba(75, 192, 192, 0.4)',
          type: 'bar',
          yAxis: 'left',
          offset: 0,
          tooltip: {
            formatter: (d) => this.currentYearTooltip(d, labelToCurrentRow[d.label], isCount),
          },
        },
      });

      // Prior year full runout line
      if (priorYear.length > 0) {
        const labelToPriorRow = {};
        labels.forEach((lbl, i) => {
          if (priorYear[i]) labelToPriorRow[lbl] = priorYear[i];
        });

        series.push({
          values: priorYear.map(rate),
          config: {
            label: `${this.year - 1} Full Runout`,
            backgroundColor: 'rgba(180, 43, 212, 0.35)',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            tooltip: {
              formatter: (d) => this.priorYearTooltip(d, labelToPriorRow[d.label], isCount, false),
            },
          },
        });

        // Prior year no-runout (EFCTV) line
        const hasEfctv = this.displayConfig.show_efctv !== false
          && priorYear.some(r => r.numerator_efctv != null);
        if (hasEfctv) {
          const efctvRate = isCount
            ? r => r.numerator_efctv || 0
            : r => (r.denominator_efctv > 0 ? (r.numerator_efctv || 0) / r.denominator_efctv : 0);

          series.push({
            values: priorYear.map(efctvRate),
            config: {
              label: `${this.year - 1} No Runout`,
              backgroundColor: 'rgba(0, 43, 212, 0.35)',
              type: 'line',
              yAxis: 'left',
              offset: 0,
              tooltip: {
                formatter: (d) => this.priorYearTooltip(d, labelToPriorRow[d.label], isCount, true),
              },
            },
          });
        }
      }

      // Monthly target glide path line
      const monthlyTargets = this.displayConfig.monthly_targets;
      if (monthlyTargets?.length === 12 && this.isRate && !isNumeratorMode) {
        const targetValues = currentYear.map(r => {
          const month = parseInt(r.observation_date.substring(5, 7), 10);
          return monthlyTargets[month - 1];
        });
        series.push({
          values: targetValues,
          config: {
            label: 'Target',
            backgroundColor: '#16a34a',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeDasharray: '8 4',
            strokeWidth: 2,
            hidePoints: true,
          },
        });
      }

      // Monthly floor line
      const monthlyFloors = this.displayConfig.monthly_floors;
      if (monthlyFloors?.length === 12 && this.isRate && !isNumeratorMode) {
        const floorValues = currentYear.map(r => {
          const month = parseInt(r.observation_date.substring(5, 7), 10);
          return monthlyFloors[month - 1];
        });
        series.push({
          values: floorValues,
          config: {
            label: 'Floor',
            backgroundColor: '#dc2626',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeDasharray: '4 3',
            strokeWidth: 1.5,
            hidePoints: true,
          },
        });
      }

      return { series, labels };
    },
    chartConfig() {
      const isNumeratorMode = this.isRate && this.viewMode === 'numerator';
      return {
        title: this.metricName + (isNumeratorMode ? ' (Numerator)' : this.isLowerBetter ? ' (Lower is Better)' : ''),
        subtitle: this.metadataSubtitle || (this.isRate && !isNumeratorMode ? 'YTD' : 'Count'),
        chartType: 'vertical_bar',
        margin: { top: 80, right: 50, bottom: 40, left: 80 },
        xAxis: {
          labelRotation: 0,
          tickFormat: this.showAllObservations
            ? (d) => format(new Date(d + 'T00:00:00'), 'MMM yyyy')
            : this.performancePeriod === 'month'
              ? (d) => format(new Date(d + 'T00:00:00'), 'MMM yyyy')
              : null,
          fontSize: 12,
        },
        yAxes: [{ position: 'left', min: 0, max: null }],
        legend: { display: true, position: isNumeratorMode ? 'left' : 'left' },
        formatting: isNumeratorMode
          ? { percentage: false, currency: false, sig_figs: 0 }
          : { percentage: this.isPercentage, currency: this.isCurrency, sig_figs: this.decimalPlaces },
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 14 },
      };
    },
  },
  watch: {
    metricIds() { this.fetchData(); },
    year() { this.fetchData(); },
    entityId() { this.fetchData(); },
  },
  methods: {
    currentYearTooltip(d, row, isCount) {
      const date = new Date(d.label + 'T00:00:00');
      const formattedDate = this.performancePeriod === 'month'
        ? format(date, 'MMM yyyy')
        : format(date, 'yyyy-MM-dd');

      const num = row ? row.numerator : null;
      const denom = row ? row.denominator : null;

      if (isCount) {
        const numStr = num != null ? num.toLocaleString() : '-';
        return `<strong>${formattedDate}</strong><br/>Count: ${numStr}`;
      }

      const rate = denom > 0 ? num / denom : null;
      let rateStr;
      if (rate == null) rateStr = '-';
      else if (this.isCurrency) rateStr = '$' + rate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      else if (this.isDecimal) rateStr = rate.toFixed(this.decimalPlaces);
      else if (this.displayConfig.format === 'per_1k') rateStr = rate.toFixed(this.decimalPlaces);
      else rateStr = (rate * 100).toFixed(this.decimalPlaces) + '%';

      const numStr = num != null ? num.toLocaleString() : '-';
      const denomStr = denom != null ? denom.toLocaleString() : '-';

      return `<strong>${formattedDate}</strong><br/>`
        + `Rate: ${rateStr}<br/>`
        + `Numerator: ${numStr}<br/>`
        + `Denominator: ${denomStr}`;
    },
    priorYearTooltip(d, priorRow, isCount, useEfctv) {
      const date = new Date(d.label + 'T00:00:00');
      const lastYear = subYears(date, 1);
      const formattedDate = this.performancePeriod === 'month'
        ? format(lastYear, 'MMM yyyy')
        : format(lastYear, 'yyyy-MM-dd');

      const num = priorRow ? (useEfctv ? priorRow.numerator_efctv : priorRow.numerator) : null;
      const denom = priorRow ? (useEfctv ? priorRow.denominator_efctv : priorRow.denominator) : null;

      if (isCount) {
        const numStr = num != null ? num.toLocaleString() : '-';
        return `<strong>${formattedDate}</strong><br/>Last Year Count: ${numStr}`;
      }

      const rate = denom > 0 ? num / denom : null;
      let rateStr;
      if (rate == null) rateStr = '-';
      else if (this.isCurrency) rateStr = '$' + rate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      else if (this.isDecimal) rateStr = rate.toFixed(this.decimalPlaces);
      else rateStr = (rate * 100).toFixed(this.decimalPlaces) + '%';

      const numStr = num != null ? num.toLocaleString() : '-';
      const denomStr = denom != null ? denom.toLocaleString() : '-';

      return `<strong>${formattedDate}</strong><br/>`
        + `Last Year Rate: ${rateStr}<br/>`
        + `Last Year Numerator: ${numStr}<br/>`
        + `Last Year Denominator: ${denomStr}`;
    },
    aggregateToMonthly(data) {
      const grouped = groupBy(data, (item) =>
        format(new Date(item.observation_date + 'T00:00:00'), 'yyyy-MM')
      );
      return Object.keys(grouped).sort().map(monthKey => {
        const weeks = grouped[monthKey].sort((a, b) =>
          a.observation_date.localeCompare(b.observation_date)
        );
        // Use latest week in the month (most current YTD snapshot)
        const latest = weeks[weeks.length - 1];
        const [year, month] = monthKey.split('-');
        return {
          ...latest,
          observation_date: format(
            new Date(parseInt(year), parseInt(month) - 1, 1),
            'yyyy-MM-dd'
          ),
        };
      });
    },
    async fetchData() {
      if (!this.metricId) return;
      this.loading = true;
      try {
        const payload = {
          observation_year: this.year,
          time_series: true,
          group_by_contract: false,
        };
        if (this.entityId) {
          payload.entity_id = this.entityId;
        } else {
          payload.entity_type = 'Tenant';
        }
        const [metaResponse, dataResponse] = await Promise.all([
          axios.get(`api/impact_analytics/composable-metrics/${this.metricId}/`),
          axios.post(`api/impact_analytics/composable-metrics/${this.metricId}/run/`, payload),
        ]);
        this.metricMeta = metaResponse.data;
        this.timeSeriesData = dataResponse.data;
        this.chartKey += 1;
      } catch (error) {
        console.error('Error fetching trend data:', error);
      } finally {
        this.loading = false;
      }
    },
    initResizeObserver() {
      if (!this.$refs.chartContainer) return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
        this.resizeDebounceTimer = setTimeout(() => {
          if (!this.loading) this.chartKey += 1;
        }, 100);
      });
      this.resizeObserver.observe(this.$refs.chartContainer);
    },
  },
  mounted() {
    this.fetchData();
    this.initResizeObserver();
  },
  beforeUnmount() {
    if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
};
</script>
